import { ArkModule } from "@skyslit/ark-react";
import { connect } from "react-redux";
import { ComponentMap } from "@skyslit/ark-react/build/types";
import CourseEditorView from "./views/CourseEditor.view";
import { Course, Subject, LectureVideo } from "lakshya-shared";

export type CourseEditorSection =
  | "general"
  | "subjects"
  | "exams"
  | "study-plan";

export type SectionFlags = {
  general: boolean;
  subjects: boolean;
  exams: boolean;
  studyPlan: boolean;
};

export type StateType = {
  course: Course;
  haveContextUpdated: boolean;
  selectedSubjectIndex: number;
  selectedLectureIndex: number;
  addedLectureIds: any;
  removedLecturesIds: any;
  loadedSections: SectionFlags;
  dirtySections: SectionFlags;
  loadingSection: CourseEditorSection | null;
  isSaving: boolean;
};

const emptySectionFlags = (): SectionFlags => ({
  general: false,
  subjects: false,
  exams: false,
  studyPlan: false,
});

const sectionFlagKey = (
  section: CourseEditorSection
): keyof SectionFlags => {
  if (section === "study-plan") {
    return "studyPlan";
  }
  return section;
};

const GENERAL_COURSE_KEYS = new Set([
  "title",
  "description",
  "isBestSelling",
  "discountRatePromoLabel",
  "previewVideoId",
  "previewVideoFileSizeInBytes",
  "bannerImageId",
  "bannerImageFizeSizeInBytes",
  "category",
  "tags",
  "cmsTags",
  "courseRateCardId",
  "attachedSubscriptions",
  "isVisible",
  "isExpiredCourse",
  "enableDaySchedule",
  "dayScheduleNote",
  "sortIndex",
  "previousQuestions",
  "syllabus",
]);

const dirtyFlagsForCourseKey = (key: string): Partial<SectionFlags> => {
  if (key === "subjects") {
    return { subjects: true };
  }
  if (key === "rankFiles" || key === "exams") {
    return { exams: true };
  }
  if (key === "studyPlanNotes") {
    return { studyPlan: true };
  }
  if (GENERAL_COURSE_KEYS.has(key)) {
    return { general: true };
  }
  // Unknown keys: treat as general so they are not silently dropped
  return { general: true };
};

const pickGeneralPayload = (course: any) => {
  const value: Record<string, any> = {};
  GENERAL_COURSE_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(course, key)) {
      value[key] = course[key];
    }
  });
  return value;
};

const toId = (value: any) => {
  if (value == null) {
    return value;
  }
  if (typeof value === "object" && value._id != null) {
    return value._id;
  }
  return value;
};

const normalizeSubjectsPayload = (subjects: any[]) =>
  (subjects || []).map((subject) => ({
    ...subject,
    lectures: (subject.lectures || []).map(toId),
  }));

const normalizeExamsPayload = (course: any) => ({
  rankFiles: (course.rankFiles || []).map(toId),
  exams: (course.exams || []).map((examLink: any) => ({
    ...examLink,
    exam: toId(examLink.exam),
  })),
});

export default class CourseEditorModule extends ArkModule<StateType> {
  constructor() {
    super("CourseEditorModule");

    this.useConnect(connect);
    this.getReducer = () => {
      return (state: StateType = this.initialState, action: any) => {
        switch (action.type) {
          case this.actionTypes.MARK_AS_SAVED: {
            const { value } = action.payload;
            return Object.assign({}, state, {
              haveContextUpdated: false,
              isSaving: false,
              dirtySections: emptySectionFlags(),
              course: Object.assign({}, state.course, {
                isPublished: value && value.isPublished,
                publishWarnings: value && value.publishWarnings,
              }),
            });
          }
          case this.actionTypes.SET_SAVING: {
            return Object.assign({}, state, {
              isSaving: !!action.payload.value,
            });
          }
          case this.actionTypes.ADD_COURSE: {
            const { courses } = action.payload;
            return Object.assign({}, state, {
              course: courses,
              selectedSubjectIndex: -1,
              selectedLectureIndex: -1,
              haveContextUpdated: false,
              loadedSections: Object.assign(emptySectionFlags(), {
                general: true,
              }),
              dirtySections: emptySectionFlags(),
              loadingSection: null,
            });
          }
          case this.actionTypes.CLEAR_COURSE: {
            return Object.assign({}, this.initialState);
          }
          case this.actionTypes.MERGE_COURSE_SECTION: {
            const { section, data } = action.payload as {
              section: CourseEditorSection;
              data: any;
            };
            const flag = sectionFlagKey(section);
            return Object.assign({}, state, {
              course: Object.assign({}, state.course, data),
              loadedSections: Object.assign({}, state.loadedSections, {
                [flag]: true,
              }),
              loadingSection: null,
            });
          }
          case this.actionTypes.SET_SECTION_LOADING: {
            return Object.assign({}, state, {
              loadingSection: action.payload.section,
            });
          }
          case this.actionTypes.UPDATE_COURSE: {
            const { key, value } = action.payload;
            return Object.assign({}, state, {
              haveContextUpdated: true,
              dirtySections: Object.assign(
                {},
                state.dirtySections,
                dirtyFlagsForCourseKey(key)
              ),
              course: Object.assign({}, state.course, {
                [key]: value,
              }),
            });
          }
          case this.actionTypes.ADD_SUBJECT: {
            const { value } = action.payload;
            return Object.assign({}, state, {
              haveContextUpdated: true,
              dirtySections: Object.assign({}, state.dirtySections, {
                subjects: true,
              }),
              course: Object.assign({}, state.course, {
                subjects: [...(state.course.subjects || []), value],
              }),
            });
          }
          case this.actionTypes.UPDATE_SUBJECT: {
            const { subjectIndex, key, value } = action.payload;
            return Object.assign({}, state, {
              haveContextUpdated: true,
              dirtySections: Object.assign({}, state.dirtySections, {
                subjects: true,
              }),
              course: Object.assign({}, state.course, {
                subjects: state.course.subjects.map((subject, index) => {
                  if (index === subjectIndex) {
                    return Object.assign({}, subject, {
                      [key]: value,
                    });
                  }
                  return subject;
                }),
              }),
            });
          }
          case this.actionTypes.SELECT_SUBJECT: {
            const { value } = action.payload;
            return Object.assign({}, state, {
              selectedSubjectIndex: value,
              selectedLectureIndex: -1,
            });
          }
          case this.actionTypes.ADD_SUBSCRIPTION: {
            const { value } = action.payload;

            return Object.assign({}, state, {
              haveContextUpdated: true,
              dirtySections: Object.assign({}, state.dirtySections, {
                general: true,
              }),
              course: Object.assign({}, state.course, {
                attachedSubscriptions: [
                  ...(state.course.attachedSubscriptions || []),
                  value,
                ],
              }),
            });
          }
          case this.actionTypes.UPDATE_SUBSCRIPTION: {
            const { subscriptionIndex, key, value } = action.payload;
            return Object.assign({}, state, {
              haveContextUpdated: true,
              dirtySections: Object.assign({}, state.dirtySections, {
                general: true,
              }),
              course: Object.assign({}, state.course, {
                attachedSubscriptions: state.course.attachedSubscriptions.map(
                  (subscription, index) => {
                    if (index === subscriptionIndex) {
                      return Object.assign({}, subscription, {
                        [key]: value,
                      });
                    }
                    return subscription;
                  }
                ),
              }),
            });
          }
          case this.actionTypes.REMOVE_SUBSCRIPTION: {
            const { subscriptionIndex } = action.payload;
            return Object.assign({}, state, {
              haveContextUpdated: true,
              dirtySections: Object.assign({}, state.dirtySections, {
                general: true,
              }),
              selectedSubjectIndex: -1,
              course: Object.assign({}, state.course, {
                attachedSubscriptions: state.course.attachedSubscriptions.filter(
                  (subscription, index) => index !== subscriptionIndex
                ),
              }),
            });
          }
          case this.actionTypes.REMOVE_SUBJECT: {
            const { subjectIndex } = action.payload;
            return Object.assign({}, state, {
              haveContextUpdated: true,
              dirtySections: Object.assign({}, state.dirtySections, {
                subjects: true,
              }),
              selectedSubjectIndex: -1,
              course: Object.assign({}, state.course, {
                subjects: state.course.subjects.filter(
                  (subject, index) => index !== subjectIndex
                ),
              }),
            });
          }
          case this.actionTypes.SELECT_LECTURE: {
            const { value } = action.payload;
            return Object.assign({}, state, {
              selectedLectureIndex: value,
            });
          }
          case this.actionTypes.ADD_LECTURE: {
            const { subjectId, value } = action.payload;
            return Object.assign({}, state, {
              haveContextUpdated: true,
              dirtySections: Object.assign({}, state.dirtySections, {
                subjects: true,
              }),
              course: Object.assign({}, state.course, {
                subjects: state.course.subjects.map((subject, index) => {
                  if (index === subjectId) {
                    return Object.assign({}, subject, {
                      lectures: [...subject.lectures, value],
                    });
                  }

                  return subject;
                }),
              }),
            });
          }
          case this.actionTypes.UPDATE_LECTURE: {
            const { subjectId, lectureId, key, value } = action.payload;
            return Object.assign({}, state, {
              haveContextUpdated: true,
              dirtySections: Object.assign({}, state.dirtySections, {
                subjects: true,
              }),
              course: Object.assign({}, state.course, {
                subjects: state.course.subjects.map((subject, index) => {
                  if (index === subjectId) {
                    return Object.assign({}, subject, {
                      lectures: subject.lectures.map(
                        (lecture, lectureIndex) => {
                          if (lectureIndex === lectureId) {
                            return Object.assign({}, lecture, {
                              [key]: value,
                            });
                          }
                          return lecture;
                        }
                      ),
                    });
                  }

                  return subject;
                }),
              }),
            });
          }
          case this.actionTypes.REMOVE_LECTURE: {
            const { subjectId, lectureId } = action.payload;
            return Object.assign({}, state, {
              haveContextUpdated: true,
              dirtySections: Object.assign({}, state.dirtySections, {
                subjects: true,
              }),
              selectedLectureIndex: -1,
              course: Object.assign({}, state.course, {
                subjects: state.course.subjects.map((subject, index) => {
                  if (index === subjectId) {
                    return Object.assign({}, subject, {
                      lectures: subject.lectures.filter(
                        (lecture, lectureIndex) => lectureIndex !== lectureId
                      ),
                    });
                  }
                  return subject;
                }),
              }),
            });
          }
          case this.actionTypes.ADD_LECTUREIDS: {
            const { value } = action.payload;
            return Object.assign({}, state, {
              addedLectureIds: [...state.addedLectureIds, value],
            });
          }
          case this.actionTypes.ADD_REMOVED_LECTUREIDS: {
            const { value } = action.payload;
            return Object.assign({}, state, {
              removedLecturesIds: [...state.removedLecturesIds, value],
            });
          }
          case this.actionTypes.CLEAR_ADDED_AND_REMOVED_LECTUREIDS: {
            return Object.assign({}, state, {
              removedLecturesIds: [],
              addedLectureIds: [],
            });
          }
          case this.actionTypes.REMOVE_ADDED_LECTUREID: {
            const { lectureId } = action.payload;
            return Object.assign({}, state, {
              addedLectureIds: state.addedLectureIds.filter(
                (id: any) => id !== lectureId
              ),
            });
          }
          case this.actionTypes.REMOVE_REMOVED_LECTUREID: {
            const { lectureId } = action.payload;
            return Object.assign({}, state, {
              removedLecturesIds: state.removedLecturesIds.filter(
                (id: any) => id !== lectureId
              ),
            });
          }
          default: {
            return state;
          }
        }
      };
    };

    this.main = () => {};
  }
  controller = {
    saveCourse: () => {
      return new Promise((resolve, reject) => {
        if (this.getState().isSaving) {
          reject(new Error("Save already in progress"));
          return;
        }

        const state = this.getState();
        const courseToUpdate = state.course as any;
        const dirty = state.dirtySections;
        const loaded = state.loadedSections;
        const id = courseToUpdate && courseToUpdate._id;

        if (!id) {
          reject(new Error("Course not loaded"));
          return;
        }

        this.dispatch({
          type: this.actionTypes.SET_SAVING,
          payload: { value: true },
        });

        const updates: Array<() => Promise<any>> = [];

        if (dirty.general) {
          if (!loaded.general) {
            this.dispatch({
              type: this.actionTypes.SET_SAVING,
              payload: { value: false },
            });
            reject(new Error("Cannot save general section before it is loaded"));
            return;
          }
          updates.push(() =>
            this.services.updateCourseSection(
              id,
              "general",
              pickGeneralPayload(courseToUpdate)
            )
          );
        }

        if (dirty.subjects) {
          if (!loaded.subjects) {
            this.dispatch({
              type: this.actionTypes.SET_SAVING,
              payload: { value: false },
            });
            reject(
              new Error("Cannot save subjects section before it is loaded")
            );
            return;
          }
          updates.push(() =>
            this.services.updateCourseSection(id, "subjects", {
              subjects: normalizeSubjectsPayload(courseToUpdate.subjects || []),
            })
          );
        }

        if (dirty.exams) {
          if (!loaded.exams) {
            this.dispatch({
              type: this.actionTypes.SET_SAVING,
              payload: { value: false },
            });
            reject(new Error("Cannot save exams section before it is loaded"));
            return;
          }
          updates.push(() =>
            this.services.updateCourseSection(
              id,
              "exams",
              normalizeExamsPayload(courseToUpdate)
            )
          );
        }

        if (dirty.studyPlan) {
          if (!loaded.studyPlan) {
            this.dispatch({
              type: this.actionTypes.SET_SAVING,
              payload: { value: false },
            });
            reject(
              new Error("Cannot save study-plan section before it is loaded")
            );
            return;
          }
          updates.push(() =>
            this.services.updateCourseSection(id, "study-plan", {
              studyPlanNotes: courseToUpdate.studyPlanNotes || [],
            })
          );
        }

        const runUpdates =
          updates.length > 0
            ? updates.reduce(
                (chain, next) => chain.then(() => next()),
                Promise.resolve(null)
              )
            : Promise.resolve(null);

        runUpdates
          .then((response: any) => {
            return this.services
              .attachCourseToLecture(
                id,
                this.getState().addedLectureIds,
                this.getState().removedLecturesIds
              )
              .then(() => {
                this.controller.clearAddedAndRemovedLectureIds();
                return response;
              })
              .catch(() => response);
          })
          .then((response: any) => {
            this.dispatch({
              type: this.actionTypes.MARK_AS_SAVED,
              payload: {
                value: response || courseToUpdate,
              },
            });
            resolve(response || courseToUpdate);
          })
          .catch((e) => {
            this.dispatch({
              type: this.actionTypes.SET_SAVING,
              payload: { value: false },
            });
            this.showError(
              "Update course failed",
              e.message ? e.message : "Network error",
              true
            );
            reject(e);
          });
      });
    },
    setCourse: (courses: Course) => {
      this.dispatch({
        type: this.actionTypes.ADD_COURSE,
        payload: {
          courses,
        },
      });
    },
    clearCourse: () => {
      this.dispatch({
        type: this.actionTypes.CLEAR_COURSE,
      });
    },
    mergeCourseSection: (section: CourseEditorSection, data: any) => {
      this.dispatch({
        type: this.actionTypes.MERGE_COURSE_SECTION,
        payload: {
          section,
          data,
        },
      });
    },
    setSectionLoading: (section: CourseEditorSection | null) => {
      this.dispatch({
        type: this.actionTypes.SET_SECTION_LOADING,
        payload: {
          section,
        },
      });
    },
    ensureCourseSection: (id: string, section: CourseEditorSection) => {
      const flag = sectionFlagKey(section);
      const state = this.getState();
      const currentId =
        state.course && (state.course as any)._id
          ? String((state.course as any)._id)
          : null;
      const sameCourse = currentId === String(id);

      if (sameCourse && state.loadedSections[flag]) {
        return Promise.resolve(state.course);
      }
      this.controller.setSectionLoading(section);
      return this.services
        .getCourseSection(id, section)
        .then((data: any) => {
          if (section === "general") {
            this.controller.setCourse(data);
          } else {
            // Do not merge section data onto a different course
            const latest = this.getState().course as any;
            if (!latest || String(latest._id) !== String(id)) {
              this.controller.setSectionLoading(null);
              return data;
            }
            this.controller.mergeCourseSection(section, data);
          }
          return data;
        })
        .catch((e) => {
          this.controller.setSectionLoading(null);
          throw e;
        });
    },
    updateCourse: (key: string, value: any) => {
      this.dispatch({
        type: this.actionTypes.UPDATE_COURSE,
        payload: {
          key,
          value,
        },
      });
    },
    addSubject: (subject: Subject) => {
      this.dispatch({
        type: this.actionTypes.ADD_SUBJECT,
        payload: {
          value: subject,
        },
      });
    },
    selectSubject: (index: number) => {
      this.dispatch({
        type: this.actionTypes.SELECT_SUBJECT,
        payload: {
          value: index,
        },
      });
    },
    updateSubject: (subjectIndex: number, key: string, value: any) => {
      this.dispatch({
        type: this.actionTypes.UPDATE_SUBJECT,
        payload: {
          key,
          value,
          subjectIndex,
        },
      });
    },
    addSubscription: (subscription: any) => {
      this.dispatch({
        type: this.actionTypes.ADD_SUBSCRIPTION,
        payload: {
          value: subscription,
        },
      });
    },
    updateSubscription: (subscriptionIndex: number, key: string, value: any) => {
      this.dispatch({
        type: this.actionTypes.UPDATE_SUBSCRIPTION,
        payload: {
          key,
          value,
          subscriptionIndex,
        },
      });
    },
    removeSubscription: (subscriptionIndex: number) => {
      this.dispatch({
        type: this.actionTypes.REMOVE_SUBSCRIPTION,
        payload: {
          subscriptionIndex,
        },
      });
    },
    selectLecture: (index: number) => {
      this.dispatch({
        type: this.actionTypes.SELECT_LECTURE,
        payload: {
          value: index,
        },
      });
    },
    addLecture: (subjectId: number, lecture: LectureVideo) => {
      this.dispatch({
        type: this.actionTypes.ADD_LECTURE,
        payload: {
          subjectId,
          value: lecture,
        },
      });
    },
    updateLecture: (
      subjectId: number,
      lectureId: number,
      key: string,
      value: any
    ) => {
      this.dispatch({
        type: this.actionTypes.UPDATE_LECTURE,
        payload: {
          subjectId,
          lectureId,
          key,
          value,
        },
      });
    },
    removeLecture: (subjectId: number, lectureId: number) => {
      this.dispatch({
        type: this.actionTypes.REMOVE_LECTURE,
        payload: {
          subjectId,
          lectureId,
        },
      });
    },
    removeSubject: (subjectIndex: number) => {
      this.dispatch({
        type: this.actionTypes.REMOVE_SUBJECT,
        payload: {
          subjectIndex,
        },
      });
    },
    addLectureIds: (id: String) => {
      if (this.getState().removedLecturesIds.indexOf(id) > -1) {
        this.dispatch({
          type: this.actionTypes.REMOVE_REMOVED_LECTUREID,
          payload: {
            lectureId: id,
          },
        });
      }
      this.dispatch({
        type: this.actionTypes.ADD_LECTUREIDS,
        payload: {
          value: id,
        },
      });
    },
    addRemovedLectureIds: (id: any) => {
      if (this.getState().addedLectureIds.indexOf(id) > -1) {
        this.dispatch({
          type: this.actionTypes.REMOVE_ADDED_LECTUREID,
          payload: {
            lectureId: id,
          },
        });
      }
      this.dispatch({
        type: this.actionTypes.ADD_REMOVED_LECTUREIDS,
        payload: {
          value: id,
        },
      });
    },
    clearAddedAndRemovedLectureIds: () => {
      this.dispatch({
        type: this.actionTypes.CLEAR_ADDED_AND_REMOVED_LECTUREIDS,
      });
    },

    removeAddedLectureId: (id: String) => {
      this.dispatch({
        type: this.actionTypes.REMOVE_ADDED_LECTUREID,
        payload: {
          lectureId: id,
        },
      });
    },
  };

  actionTypes = {
    MARK_AS_SAVED: "MARK_AS_SAVED",
    SET_SAVING: "SET_SAVING",
    ADD_COURSE: "ADD_COURSE",
    CLEAR_COURSE: "CLEAR_COURSE",
    MERGE_COURSE_SECTION: "MERGE_COURSE_SECTION",
    SET_SECTION_LOADING: "SET_SECTION_LOADING",
    UPDATE_COURSE: "UPDATE_COURSE",
    ADD_SUBJECT: "ADD_SUBJECT",
    UPDATE_SUBJECT: "UPDATE_SUBJECT",
    SELECT_SUBJECT: "SELECT_SUBJECT",
    ADD_SUBSCRIPTION: "ADD_SUBSCRIPTION",
    UPDATE_SUBSCRIPTION: "UPDATE_SUBSCRIPTION",
    ADD_LECTURE: "ADD_LECTURE",
    SELECT_LECTURE: "SELECT_LECTURE",
    UPDATE_LECTURE: "UPDATE_LECTURE",
    REMOVE_SUBJECT: "REMOVE_SUBJECT",
    REMOVE_SUBSCRIPTION: "REMOVE_SUBSCRIPTION",
    REMOVE_LECTURE: "REMOVE_LECTURE",
    ADD_LECTUREIDS: "ADD_LECTUREIDS",
    ADD_REMOVED_LECTUREIDS: "ADD_REMOVED_LECTUREIDS",
    CLEAR_ADDED_AND_REMOVED_LECTUREIDS: "CLEAR_ADDED_AND_REMOVED_LECTUREIDS",
    REMOVE_ADDED_LECTUREID: "REMOVE_ADDED_LECTUREID",
    REMOVE_REMOVED_LECTUREID: "REMOVE_REMOVED_LECTUREID",
  };

  services = {
    getCourseById: (id: string) => {
      return new Promise((resolve, reject) => {
        this.getServiceProvider("Main")
          .get(`/api/admin/courses/${id}`)
          .then((response) => {
            resolve((response.data && response.data) || null);
          })
          .catch((err) => {
            reject((err.response && err.response && err.response.data) || err);
          });
      });
    },
    getCourseSection: (id: string, section: CourseEditorSection) => {
      return new Promise((resolve, reject) => {
        this.getServiceProvider("Main")
          .get(`/api/admin/courses/${id}/sections/${section}`)
          .then((response) => {
            resolve((response.data && response.data) || null);
          })
          .catch((err) => {
            reject((err.response && err.response && err.response.data) || err);
          });
      });
    },
    updateCourseById: (id: string, value: Course) => {
      return new Promise((resolve, reject) => {
        this.getServiceProvider("Main")
          .put(`/api/admin/courses/${id}`, { value: value })
          .then((response) => {
            resolve((response.data && response.data) || null);
          })
          .catch((err) => {
            reject((err.response && err.response && err.response.data) || err);
          });
      });
    },
    updateCourseSection: (
      id: string,
      section: CourseEditorSection,
      value: any
    ) => {
      return new Promise((resolve, reject) => {
        this.getServiceProvider("Main")
          .put(`/api/admin/courses/${id}/sections/${section}`, { value })
          .then((response) => {
            resolve((response.data && response.data) || null);
          })
          .catch((err) => {
            reject((err.response && err.response && err.response.data) || err);
          });
      });
    },
    deleteCourseById: (id: string) => {
      return new Promise((resolve, reject) => {
        this.getServiceProvider("Main")
          .delete(`/api/admin/courses/${id}`)
          .then((response) => {
            resolve((response.data && response.data) || null);
          })
          .catch((err) => {
            reject((err.response && err.response && err.response.data) || err);
          });
      });
    },

    attachCourseToLecture: (
      id: string,
      addedLectureIds: [],
      removedLectureIds: []
    ) => {
      return new Promise((resolve, reject) => {
        this.getServiceProvider("Main")
          .post(`/api/admin/lecture/attach-course/${id}`, {
            addedLectureIds,
            removedLectureIds,
          })
          .then((response) => {
            resolve((response.data && response.data) || null);
          })
          .catch((err) => {
            reject((err.response && err.response && err.response.data) || err);
          });
      });
    },
  };
  initialState: StateType = {
    course: null,
    haveContextUpdated: false,
    selectedSubjectIndex: -1,
    selectedLectureIndex: -1,
    addedLectureIds: [],
    removedLecturesIds: [],
    loadedSections: emptySectionFlags(),
    dirtySections: emptySectionFlags(),
    loadingSection: null,
    isSaving: false,
  };

  views: ComponentMap = {
    CourseEditor: CourseEditorView,
  };
}
